import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../config/api";
import { generateQueryParams } from "../../utils/generateQueryParams";

export const getAllAIWorkflowSubscriptions = createAsyncThunk(
  "aiWorkflow/getAllAIWorkflowSubscriptions",
  async (searchQuery = "") => {
    const queryParams = searchQuery
      ? `?search=${encodeURIComponent(searchQuery)}`
      : "";
    const response = await api.get(
      `/admin/ai-workflow/subscriptions${queryParams}`,
    );
    return response.data;
  },
);

// Manual Payments Actions

export const confirmAIWorkflowManualPayment = createAsyncThunk(
  "aiWorkflow/confirmAIWorkflowManualPayment",
  async (paymentId) => {
    const response = await api.post(`/admin/ai-workflow/subscriptions/manual/confirm/${paymentId}`);
    return response.data;
  },
);

export const rejectAIWorkflowManualPayment = createAsyncThunk(
  "aiWorkflow/rejectAIWorkflowManualPayment",
  async (paymentId) => {
    const response = await api.post(`/admin/ai-workflow/subscriptions/manual/reject/${paymentId}`);
    return response.data;
  },
);

/**
 * Get activity logs for a project with filters
 */
export const getAiworkFlowActivityLogs = createAsyncThunk(
  "aiWorkflow/getAiworkFlowActivityLogs",
  async ({ projectId, filters }) => {
    const query = generateQueryParams(filters);
    const response = await api.get(
      `/admin/ai-workflow/subscriptions/${projectId}/activity-logs?${query}`,
    );
    return response.data;
  },
);

// Get AI Workflow Plan by Id
export const getAiWorkflowPlanById = createAsyncThunk(
  "aiWorkflow/getAiWorkflowPlanById",
  async (planId) => {
    const response = await api.get(
      `/admin/ai-workflow/subscriptions/${planId}`,
    );
    return response.data;
  },
);

// Update Subscription Customization (Features & Pricing)
export const updateSubscriptionCustomization = createAsyncThunk(
  "aiWorkflow/updateSubscriptionCustomization",
  async (
    {
      subscriptionId,
      customFeatures,
      customAmount,
      effectiveDate,
      applyImmediately,
      reason,
      notes,
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.put(
        `/admin/ai-workflow/subscriptions/${subscriptionId}/features`,
        {
          customFeatures,
          customAmount,
          effectiveDate,
          applyImmediately,
          reason,
          notes,
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message },
      );
    }
  },
);

// =========== AI Workflow Comments ===========

// Get AI Workflow Comments
export const getAiWorkflowComments = createAsyncThunk(
  "aiWorkflow/getAiWorkflowComments",
  async (planId) => {
    const response = await api.get(
      `/admin/ai-workflow/subscriptions/${planId}/comments`,
    );
    return response.data;
  },
);

// Add AI Workflow Comment
export const addAiWorkflowComment = createAsyncThunk(
  "aiWorkflow/addAiWorkflowComment",
  async (commentData) => {
    const response = await api.post(
      "/admin/ai-workflow/subscriptions/comment",
      commentData,
    );
    return response.data;
  },
);

// Add AI Workflow Reply
export const addAiWorkflowReply = createAsyncThunk(
  "aiWorkflow/addAiWorkflowReply",
  async ({ commentId, replyData }) => {
    const response = await api.post(
      `/admin/ai-workflow/subscriptions/comment/${commentId}/reply`,
      replyData,
    );
    return response.data;
  },
);

// ======= AI Workflow Team =======

// Get Available AI Workflow Team
export const getAvailableAiWorkflowAdminTeam = createAsyncThunk(
  "aiWorkflow/getAvailableAiWorkflowAdminTeam",
  async (planId) => {
    const response = await api.get(
      `/admin/ai-workflow/subscriptions/${planId}/available-admin-members`,
    );
    return response.data.data;
  },
);

// Add AI Workflow Team
export const addAiWorkflowAdminTeam = createAsyncThunk(
  "aiWorkflow/addAiWorkflowAdminTeam",
  async ({ projectId, memberId, role, permissionType }) => {
    const response = await api.post(
      `/admin/ai-workflow/subscriptions/${projectId}/admin-members`,
      { memberId, role, permissionType },
    );
    return response.data.data;
  },
);

// Remove AI Workflow Team
export const removeAiWorkflowTeam = createAsyncThunk(
  "aiWorkflow/removeAiWorkflowTeam",
  async (teamId) => {
    const response = await api.delete(
      `/admin/ai-workflow/subscriptions/team-members/${teamId}`,
    );
    return response.data;
  },
);

// ======= AI Workflow Tickets =======

// Add Comment to AI Workflow Ticket
export const addAiWorkflowTicketComment = createAsyncThunk(
  "aiWorkflow/addAiWorkflowTicketComment",
  async ({
    ticketId,
    content,
    attachments = [],
    isNestedReply = false,
    parentCommentId = null,
  }) => {
    const response = await api.post(
      `/admin/ai-workflow/subscriptions/tickets/${ticketId}/comments`,
      { content, attachments, isNestedReply, parentCommentId },
    );
    return response.data;
  },
);

// ========== ADMIN TICKET MANAGEMENT ==========

/**
 * Update ticket status (admin only)
 */
export const updateAiWorkflowTicketStatus = createAsyncThunk(
  "aiWorkflow/updateTicketStatus",
  async ({ ticketId, status, notes }) => {
    const response = await api.patch(
      `/admin/ai-workflow/subscriptions/tickets/${ticketId}/status`,
      { status, notes },
    );
    return response.data.data;
  },
);

/**
 * Update ticket priority (admin only)
 */
export const updateAiWorkflowTicketPriority = createAsyncThunk(
  "aiWorkflow/updateTicketPriority",
  async ({ ticketId, priority }) => {
    const response = await api.patch(
      `/admin/ai-workflow/subscriptions/tickets/${ticketId}/priority`,
      { priority },
    );
    return response.data.data;
  },
);

/**
 * Assign ticket to admin members (admin only)
 */
export const assignAiWorkflowTicketToAdmins = createAsyncThunk(
  "aiWorkflow/assignTicket",
  async ({ ticketId, adminIds }) => {
    const response = await api.post(
      `/admin/ai-workflow/subscriptions/tickets/${ticketId}/assign`,
      { adminIds },
    );
    return response.data.data;
  },
);

// ====== AI Workflow File Attachments ======

// Add File Attachment to AI Workflow Ticket
export const addAiWorkflowFileAttachment = createAsyncThunk(
  "aiWorkflow/addAiWorkflowFileAttachment",
  async ({ projectId, files, note, category }) => {
    const response = await api.post(
      `/admin/ai-workflow/subscriptions/${projectId}/upload-files`,
      { files, note, category },
    );
    return response.data;
  },
);
